import { useCan } from '@refinedev/core';
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Loading, StatusDot } from '../components/ui';
import { fetchNodeDetail, type NodeDetail } from '../data/nodes';

// Import dinámico y no estático: `NodeTerminal` carga xterm.js entero, y esta
// página es justo la que menos debería forzarlo al bundle principal. Es el
// mismo patrón que usa `NodeDetailPage.tsx` para su pestaña de terminal — un
// import estático aquí uniría ambos puntos de entrada en un solo chunk y
// dejaría a todo el panel cargando xterm.js desde el primer render.
const NodeTerminal = lazy(async () => ({
  default: (await import('../components/NodeTerminal')).NodeTerminal,
}));

/**
 * Ventana de terminal sola, sin sidebar ni cabecera del panel.
 *
 * Es adonde apunta "Abrir en otra ventana" de `NodeTerminal`. Si ese botón
 * abriera la ficha completa (`/nodes/:id?tab=terminal`), la ventana nueva
 * enseñaría el panel entero — sidebar, navegación, pestañas — encogido en una
 * ventana pequeña, en vez de una terminal dedicada. Por eso vive en una ruta
 * hermana del catch-all de `App.tsx`, fuera de `<Layout>`.
 */
export function TerminalStandalonePage() {
  const { nodeId = '' } = useParams();
  const [detail, setDetail] = useState<NodeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchNodeDetail(nodeId)
      .then((result) => {
        if (!cancelled) setDetail(result);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'No se pudo cargar el nodo');
      });
    return () => {
      cancelled = true;
    };
  }, [nodeId]);

  const resourcePath = useMemo(
    () => ({
      tenantId: detail?.node.tenantId ?? '',
      branchId: detail?.node.branchId,
      nodeId,
    }),
    [detail?.node.tenantId, detail?.node.branchId, nodeId],
  );

  const { data: permission } = useCan({
    resource: 'actions',
    action: 'terminal.open',
    params: { path: resourcePath },
  });

  if (error) {
    return (
      <div className="p-4">
        <Alert>{error}</Alert>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="grid min-h-screen place-items-center">
        <Loading label="Cargando nodo…" />
      </div>
    );
  }

  return (
    <div className="grid min-h-screen gap-3 bg-background p-3 text-foreground">
      <header className="flex items-center gap-2 px-1">
        <StatusDot status={detail.node.status} />
        <h1 className="text-sm font-semibold tracking-tight">{detail.node.alias}</h1>
        <span className="text-xs opacity-50">Terminal</span>
      </header>

      <Suspense fallback={<Loading label="Cargando el emulador de terminal…" />}>
        <NodeTerminal
          nodeId={nodeId}
          alias={detail.node.alias}
          online={detail.node.status === 'online'}
          disabledReason={permission?.can ? null : (permission?.reason ?? 'Sin permiso')}
        />
      </Suspense>
    </div>
  );
}
